import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getRewards, redeemReward, getMyRedemptions, getUserProfile } from '../services/api';
import RewardWallet from './RewardWallet';
import './Rewards.css';

function Rewards() {
  const { user, login } = useContext(AuthContext);
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('catalog');
  const [selectedRedemption, setSelectedRedemption] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rewardsRes, redemptionsRes] = await Promise.all([
        getRewards(),
        getMyRedemptions()
      ]);

      setRewards(rewardsRes.data);
      setRedemptions(redemptionsRes.data);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (rewardId) => {
    setRedeeming(rewardId);
    setMessage('');

    try {
      const response = await redeemReward(rewardId);
      setMessage(`✅ ${response.data.message}`);
      
      // Update user context
      const updatedProfile = await getUserProfile();
      login(localStorage.getItem('token'), updatedProfile.data);
      
      // Refresh data
      fetchData();
      
      // Switch to redemptions tab
      setTimeout(() => setActiveTab('redemptions'), 1500);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.error || 'Redemption failed'}`);
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) {
    return <div className="container"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="container rewards-page">
      <div className="page-header">
        <h1>🎁 Rewards Catalog</h1>
        <p>Redeem your points for exclusive rewards</p>
        <div className="user-points-display">
          <span className="points-label">Your Points:</span>
          <span className="points-value">{user?.points || 0}</span>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'catalog' ? 'active' : ''}`}
          onClick={() => setActiveTab('catalog')}
        >
          Rewards Catalog
        </button>
        <button 
          className={`tab ${activeTab === 'redemptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('redemptions')}
        >
          My Redemptions ({redemptions.length})
        </button>
      </div>

      {activeTab === 'catalog' ? (
        <div className="rewards-grid">
          {rewards.map(reward => {
            const canAfford = user?.points >= reward.pointsCost;
            return (
              <div key={reward.id} className={`reward-card ${!canAfford ? 'disabled' : ''}`}>
                <div className="reward-icon">{reward.image}</div>
                <h3>{reward.name}</h3>
                <p className="reward-description">{reward.description}</p>
                <div className="reward-cost">
                  <span className="cost-value">{reward.pointsCost}</span>
                  <span className="cost-label">points</span>
                </div>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleRedeem(reward.id)}
                  disabled={!canAfford || redeeming === reward.id}
                >
                  {redeeming === reward.id ? 'Redeeming...' : canAfford ? 'Redeem' : 'Not Enough Points'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="redemptions-list">
          {redemptions.length === 0 ? (
            <div className="empty-state">
              <p>You haven't redeemed any rewards yet.</p>
              <button className="btn btn-primary" onClick={() => setActiveTab('catalog')}>
                Browse Rewards
              </button>
            </div>
          ) : (
            redemptions.map(redemption => (
              <div key={redemption.id} className="redemption-card">
                <div className="redemption-header">
                  <h3>{redemption.rewardName}</h3>
                  <span className={`status-badge ${redemption.status}`}>
                    {redemption.status}
                  </span>
                </div>
                <div className="redemption-details">
                  <div className="detail-item">
                    <span className="detail-label">Code:</span>
                    <span className="detail-value code">{redemption.code}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Points Used:</span>
                    <span className="detail-value">{redemption.pointsCost}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Redeemed:</span>
                    <span className="detail-value">
                      {new Date(redemption.redeemedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Expires:</span>
                    <span className="detail-value">
                      {new Date(redemption.expiresAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="redemption-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => setSelectedRedemption(redemption)}
                  >
                    📱 View QR Code & Wallet
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {selectedRedemption && (
        <RewardWallet 
          redemption={selectedRedemption}
          onClose={() => setSelectedRedemption(null)}
        />
      )}
    </div>
  );
}

export default Rewards;
